import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Building, HandHeart, ArrowRight, CheckCircle, Send } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const whyJoin = [
  "Opportunity to contribute to meaningful social change",
  "Be part of a structured and growing organization",
  "Engage in initiatives that combine inner well-being and social impact",
  "Collaborate with like-minded individuals and institutions",
];

const volunteerOpportunities = [
  "Assisting in awareness campaigns",
  "Supporting health camps and community drives",
  "Conducting or assisting in digital literacy sessions",
  "Helping with documentation and outreach activities",
];

const partnerWho = [
  "Corporates (CSR initiatives)",
  "Educational institutions",
  "Healthcare providers",
  "NGOs and social organizations",
];

const partnerAreas = [
  "Healthcare camps",
  "Digital literacy programs",
  "Community welfare initiatives",
  "Awareness campaigns",
];

const csrReasons = [
  "Clear program structure and scalability",
  "Focus on healthcare, education, digital empowerment, and sustainability",
  "Transparent governance and reporting",
  "Community-driven execution model",
];

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setIsVisible(true);
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, isVisible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isVisible] = useInView();
  return (
    <div ref={ref} className={`${className} transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function BulletList({ items, color = "#606C38" }) {
  return (
    <ul className="space-y-2">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <CheckCircle size={14} className="mt-0.5 shrink-0" style={{ color }} strokeWidth={1.5} />
          <span className="text-[#7A827E] text-sm">{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function GetInvolvedPage() {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div data-testid="get-involved-page" className="pt-20">
      {/* SECTION 1 — OPENING */}
      <section className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#1B201E] mb-6">Get Involved</h1>
            <p className="text-[#7A827E] text-base sm:text-lg max-w-4xl leading-relaxed">
              Real change begins when individuals come together with a shared purpose. At BSM Welfare Foundation, we invite you to be a part of a movement that combines awareness, compassion, and action to create meaningful and lasting impact.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 2 — WHY JOIN US */}
      <section data-testid="why-join" className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection className="max-w-3xl">
            <p className="overline mb-3">Why Get Involved</p>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1B201E] mb-4">
              Why Get Involved with BSM Foundation?
            </h2>
            <p className="text-[#7A827E] text-base mb-6">We are building more than programs — we are building a conscious and empowered society.</p>
            <BulletList items={whyJoin} />
          </AnimatedSection>
        </div>
      </section>

      {/* INVOLVEMENT SECTIONS */}
      <section data-testid="involvement-details" className="pb-20 sm:pb-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 space-y-10">
          {/* Volunteer */}
          <AnimatedSection>
            <Card data-testid="volunteer-section" className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <CardContent className="p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-[#E28743]/10 flex items-center justify-center"><Heart size={20} className="text-[#E28743]" strokeWidth={1.5} /></div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1B201E]">Volunteer With Us</h3>
                </div>
                <p className="text-[#7A827E] text-sm leading-relaxed mb-5">
                  Become a part of our on-ground and digital initiatives and contribute your time, skills, and energy to create real impact.
                </p>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Opportunities Include</p>
                <BulletList items={volunteerOpportunities} />
              </CardContent>
            </Card>
          </AnimatedSection>

          {/* Partner */}
          <AnimatedSection>
            <Card data-testid="partner-section" className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <CardContent className="p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-[#606C38]/10 flex items-center justify-center"><Users size={20} className="text-[#606C38]" strokeWidth={1.5} /></div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1B201E]">Partner With Us</h3>
                </div>
                <p className="text-[#7A827E] text-sm leading-relaxed mb-5">
                  We believe collaboration is the key to scalable impact. BSM Welfare Foundation actively seeks partnerships with organizations, institutions, and community groups.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#606C38] mb-2">Who Can Partner</p>
                    <BulletList items={partnerWho} />
                  </div>
                  <div>
                    <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#606C38] mb-2">Collaboration Areas</p>
                    <BulletList items={partnerAreas} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </AnimatedSection>

          {/* CSR */}
          <AnimatedSection>
            <Card data-testid="csr-section" className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <CardContent className="p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 rounded-xl bg-[#8ECAE6]/20 flex items-center justify-center"><Building size={20} className="text-[#023047]" strokeWidth={1.5} /></div>
                  <h3 className="font-['Outfit'] text-xl font-semibold text-[#1B201E]">CSR Collaboration</h3>
                </div>
                <p className="text-[#7A827E] text-sm leading-relaxed mb-5">
                  As a Section 8 registered organization, BSM Welfare Foundation offers a structured and transparent platform for CSR partnerships aligned with national development goals.
                </p>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#E28743] mb-2">Why Partner for CSR</p>
                <BulletList items={csrReasons} />
              </CardContent>
            </Card>
          </AnimatedSection>

          {/* Donate */}
          <AnimatedSection>
            <Card data-testid="donate-section" className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <CardContent className="p-8 sm:p-10 text-center">
                <div className="w-14 h-14 rounded-2xl bg-[#E28743]/10 flex items-center justify-center mx-auto mb-5">
                  <HandHeart size={26} className="text-[#E28743]" strokeWidth={1.5} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-[#1B201E] mb-3">Support Our Mission</h3>
                <p className="text-[#7A827E] text-sm leading-relaxed max-w-md mx-auto">
                  Your support enables us to expand our reach and bring meaningful change to more communities. Donation facility coming soon.
                </p>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 3 — ENGAGEMENT FORM */}
      <section data-testid="engagement-form-section" className="py-20 sm:py-28 bg-[#F3F0E6]">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <AnimatedSection>
            <Card className="border-0 rounded-3xl shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
              <CardContent className="p-8 sm:p-10">
                <h2 className="font-['Outfit'] text-2xl font-semibold text-[#1B201E] mb-2">Get In Touch</h2>
                <p className="text-sm text-[#7A827E] mb-8">Fill in the form below and our team will reach out to you.</p>
                <form onSubmit={handleSubmit} className="space-y-5" data-testid="engagement-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Full Name</label>
                      <Input data-testid="form-name" placeholder="Your full name" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Email</label>
                      <Input data-testid="form-email" type="email" placeholder="your@email.com" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Phone Number</label>
                    <Input data-testid="form-phone" type="tel" placeholder="+91 XXXXX XXXXX" className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Area of Interest</label>
                    <select data-testid="form-interest" className="w-full rounded-xl border border-[#E2E5E3] bg-white px-4 py-3 text-sm focus:border-[#E28743] focus:ring-2 focus:ring-[#E28743]/20 transition-all outline-none">
                      <option value="">Select an area</option>
                      <option>Volunteer</option>
                      <option>Partnership</option>
                      <option>CSR</option>
                      <option>General Inquiry</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2C3531] mb-1.5 block">Message</label>
                    <Textarea data-testid="form-message" placeholder="Your message..." className="rounded-xl border-[#E2E5E3] focus:border-[#E28743] focus:ring-[#E28743]/20 min-h-[100px]" />
                  </div>
                  <button type="submit" data-testid="form-submit" className="inline-flex items-center gap-2 bg-[#E28743] text-white hover:bg-[#C96A27] rounded-full px-8 py-3.5 font-medium transition-all duration-300 hover:-translate-y-1 shadow-[0_4px_14px_rgba(226,135,67,0.3)] btn-press text-sm w-full sm:w-auto justify-center">
                    {submitted ? "Submitted! Thank You" : <><Send size={16} /> Submit</>}
                  </button>
                </form>
              </CardContent>
            </Card>
          </AnimatedSection>
        </div>
      </section>

      {/* SECTION 4 — CLOSING */}
      <section className="py-20 sm:py-28 bg-[#1B201E]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <AnimatedSection>
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-semibold tracking-tight text-white mb-5">Be a Part of Something Meaningful</h2>
            <p className="text-[#7A827E] text-base leading-relaxed max-w-2xl mx-auto">
              Every small step contributes to a larger change. Whether you volunteer your time, collaborate as a partner, or support our initiatives — your involvement can help build a more aware, compassionate, and empowered society.
            </p>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
